/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Collapse, Drawer, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import classnames from 'classnames';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { AsyncResult } from '@msdyn365-commerce/core';
import { IHeaderViewProps } from './freedom-header';

const headerView: React.FC<IHeaderViewProps> = props => {
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        Divider
    } = props;
    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>

                   {/* This is the area that is majorly revamped. Subtract components as needed. */}

                        <div className='freedom-test'>
                            {props.logo}
                            {props.navIcon}
                            
                        </div>

                        <div className="freedom-test desktop-nav">
                        {_renderReactFragment(props.menuBar)}
                        </div>
                  
                        <div className='freedom-test always-flex align-items-center'>
                            {_renderReactFragment(props.search)}
                            {_renderDesktopAccountBlock(props)}
                            {props.wishListIconDesktop}
                            <Node {...Divider}/>
                            {props.cartIcon}
                            {_renderReactFragment(props.siteOptions)}
                        </div>
                    

                </Node>
                {_renderCollapseMenu(props)}
                
            </Node>
        </Module>
    );
};

function _renderCollapseMenu(props: IHeaderViewProps): JSX.Element | null {
    const { MobileMenuLinksContainer, mobileMenuCollapsed } = props;
    return (
        <Collapse className={'ms-header__collapsible-hamburger'} isOpen={!mobileMenuCollapsed}>
            <Node {...MobileMenuLinksContainer}>
                {_renderReactFragment(props.menuBar)}
                {_renderReactFragment(props.search)}
                {_renderMobileAccountBlock(props, true)}
                {props.wishListIconMobile}
            </Node>


        </Collapse>
    );
}

const renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
    const customer = accountInformation && accountInformation.result;
    return (
        <>{customer && customer.FirstName}</>
    );
};

function _renderMobileAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        signOutLink,
        signInLink,
        data,
        accountLinks } = props;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames('ms-header__drawer', AccountInfoDropdownParentContainer.className, renderForMobile ? 'account-mobile' : 'account-desktop');

        if (accountLinks) {
            return (
                <Drawer className={accountClassName} openGlyph='ms-header__drawer-open' closeGlyph='ms-header__drawer-close' glyphPlacement='end' toggleButtonText={renderCustomerName(data.accountInformation)}>
                    <div>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLink}
                    </div>
                </Drawer>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }
    return null;
}

function _renderDesktopAccountBlock(props: IHeaderViewProps): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks,
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        { accountLinks ? accountLinks.map(link => link) : false }
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {signInLink}
                </Node>
            );
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </React.Fragment>
    );
}

export default headerView;